import {
  Add20,
  ArrowDown16,
  ArrowUp16,
  Copy16,
  TrashCan16,
} from '@carbon/icons-react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import {
  CustomerPortalPage,
  CustomerPortalPageType,
  CustomerPortalSection,
  CustomerPortalWidgetType,
} from 'api/customer-portal/types';
import { useIsResponsive } from 'hooks/use-media-query';
import { CopyOrMoveSectionsMenu } from './copy-or-move-sections-menu';
import { CpFooter } from './cp-footer';
import { CpSectionRenderer } from './cp-section-renderer';

export interface CpPageRendererProps {
  page: CustomerPortalPage;
  projectId: number;
  isBuildMode?: boolean;
  onSectionClick?: (s: string) => void;
  // This is only used in build mode
  selectedSection?: string;
  onAddSectionAbove?: (sectionReferenceId: string) => void;
  onAddSectionBelow?: (sectionReferenceId: string) => void;
  onMoveUp?: (sectionReferenceId: string) => void;
  onMoveDown?: (sectionReferenceId: string) => void;
  onDelete?: (sectionReferenceId: string) => void;
  onCopy?: (sectionReferenceId: string, pageReferenceId) => void;
  onMove?: (sectionReferenceId: string, pageReferenceId) => void;
  onDuplicate?: (sectionReferenceId: string) => void;
  showActions?: boolean;
  emptyPlaceholder?: React.ReactNode;
  pages?: CustomerPortalPage[];
  onPageChange?: (pageRefId: string) => void;
  showFooter?: boolean;
}

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-02);
  background: var(--white-100);
  border-radius: var(--border-radius-default);
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 10;
  gap: var(--spacing-03);
  opacity: 0;
  box-shadow: var(--shadow-01);
  &:hover {
    opacity: 1;
  }
  transition: all 0.25s;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  border-radius: var(--border-radius-default);
  cursor: pointer;
  &:hover {
    background: var(--hover-color);
  }
`;

type CpPageWrapperProps = {
  pageId: string;
  pageType: CustomerPortalPageType;
};

const AddButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: black;
  color: var(--white-100);
  height: 32px;
  width: 32px;
  border-radius: 100%;
  cursor: pointer;
  z-index: 1000;
  position: absolute;
  top: -16px;
  left: 50%;
  /* transform: translateX(-50%); */
  opacity: 0;
  transition: all 0.25s;

  svg {
    fill: var(--white-100);
  }

  &:hover {
    background: var(--blue-60);
  }

  &:active {
    background: var(--blue-70);
  }
`;

const AddButtonBelow = styled(AddButton)`
  top: unset;
  bottom: -16px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  transition: all 0.25s;

  &:hover {
    ${AddButton}
    ,${AddButtonBelow}
    ,${ActionsWrapper} {
      transition: all 0.25s;

      opacity: 1;
    }
  }
`;

const MaskSection = styled.div<{ isBuildMode?: boolean }>`
  position: relative;
  transition: all 0.25s;
  z-index: 0;
  ${(props) =>
    props.isBuildMode
      ? css`
          cursor: pointer;
          * {
            pointer-events: ${props.isBuildMode ? 'none' : 'auto'} !important;
          }
        `
      : css``};
  ${(props) =>
    props.isBuildMode
      ? css`
          :before {
            transition: all 0.25s;

            content: '';
            height: 100%;
            width: 100%;
            position: absolute;
            border: 1px solid transparent;
            top: 0;
            left: 0;
            z-index: 100000;
          }
          :hover,
          ${Wrapper}:hover & {
            :before {
              border: 4px solid var(--blue-40);
              z-index: 100000;
            }
          }
        `
      : css``};

  &.rl-cp-selected-section {
    :before {
      content: '';
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      border: 4px solid var(--blue-60);
      left: 0;
      z-index: 10000;
    }

    [data-preview-mode='true'] & {
      :before {
        display: none;
      }
    }
  }
`;

const CpPageWrapper = styled.div.attrs<CpPageWrapperProps>((props) => ({
  className: 'rl-cp-page',
  'data-page-wrapper': props.pageId,
  'data-page-tyoe': props.pageType,
}))<CpPageWrapperProps>`
  --font-family: 'SF Pro Display', 'Segoe UI', Arial, sans-serif;
  background: var(--white-100);
  width: auto;

  #root:has(&) {
    height: 100%;
  }

  ${(props) =>
    props?.pageType === CustomerPortalPageType.Chat
      ? css`
          height: 100%;
          min-height: 100%;
          ${MaskSection} {
            height: 100%;
            min-height: 100%;
          }
        `
      : css`
          scroll-padding-bottom: var(--spacing-10);
          /* overflow-y: auto;
          min-height: 100%;
           */
          height: auto;
        `};
`;

export const CpPageRenderer: React.FC<CpPageRendererProps> = ({
  page,
  projectId,
  onSectionClick,
  isBuildMode,
  selectedSection,
  onAddSectionAbove,
  onAddSectionBelow,
  onDelete,
  onMoveDown,
  onMoveUp,
  onCopy,
  onMove,
  showActions,
  emptyPlaceholder,
  pages,
  onPageChange,
  onDuplicate,
  showFooter,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isResponsive = useIsResponsive();
  const isChatPageChatWidget = useCallback(
    (p: CustomerPortalPage, s: CustomerPortalSection) => {
      return (
        p?.pageType === CustomerPortalPageType.Chat &&
        s?.blocks?.[0]?.widget?.widgetType === CustomerPortalWidgetType.CHATS
      );
    },
    [],
  );
  const sections: CustomerPortalPage['sections'] = useMemo(
    () =>
      _.sortBy(
        page?.sections?.filter((e) => !e?.deleted),
        (a) => {
          return page?.pageMeta?.sectionsOrder?.indexOf?.(
            a?.sectionReferenceId,
          );
        },
      ) ?? [],
    [page],
  );

  return (
    <CpPageWrapper pageId={page.pageReferenceId} pageType={page.pageType}>
      {showFooter && !isResponsive ? <CpFooter /> : <></>}
      {sections && sections?.length
        ? sections.map((s, index) => (
            <Wrapper
              key={s?.sectionReferenceId}
              data-cy={`customer_portal_builder.section.${s?.sectionName}`}
              data-cy-section-id={s?.sectionReferenceId}
            >
              {showActions ? (
                <ActionsWrapper
                  ref={ref}
                  className="rl-cp-section-actions-wrapper"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  {onMoveUp && index !== 0 ? (
                    <Tooltip
                      title={
                        <FormattedMessage id="customer_portal.builder.move_up" />
                      }
                    >
                      <IconWrapper
                        data-cy={`customer_portal_builder.move_up.${s?.sectionName}`}
                        onClick={() => onMoveUp(s?.sectionReferenceId)}
                      >
                        <ArrowUp16 />
                      </IconWrapper>
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                  {onMoveDown && index !== sections.length - 1 ? (
                    <Tooltip
                      title={
                        <FormattedMessage id="customer_portal.builder.move_down" />
                      }
                    >
                      <IconWrapper
                        data-cy={`customer_portal_builder.move_down.${s?.sectionName}`}
                        onClick={() => onMoveDown(s?.sectionReferenceId)}
                      >
                        <ArrowDown16 />
                      </IconWrapper>
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                  {onCopy || onMove ? (
                    <CopyOrMoveSectionsMenu
                      onDuplicate={() => onDuplicate?.(s?.sectionReferenceId)}
                      currentPageRefId={page.pageReferenceId}
                      onPageClick={(p) => onPageChange?.(p?.pageReferenceId)}
                      onCopy={(p) =>
                        onCopy?.(s?.sectionReferenceId, p?.pageReferenceId)
                      }
                      onMove={(p) =>
                        onMove?.(s?.sectionReferenceId, p?.pageReferenceId)
                      }
                      pages={pages ?? []}
                    >
                      <Tooltip
                        title={
                          <FormattedMessage id="customer_portal.builder.copy_or_move" />
                        }
                      >
                        <IconWrapper
                          data-cy={`customer_portal_builder.copy_section.${s?.sectionName}`}
                        >
                          <Copy16 />
                        </IconWrapper>
                      </Tooltip>
                    </CopyOrMoveSectionsMenu>
                  ) : (
                    <></>
                  )}
                  {onDelete ? (
                    <Tooltip
                      title={
                        <FormattedMessage id="customer_portal.builder.delete" />
                      }
                    >
                      <IconWrapper
                        data-cy={`customer_portal_builder.delete.${s?.sectionName}`}
                        onClick={() => onDelete(s?.sectionReferenceId)}
                      >
                        <TrashCan16 />
                      </IconWrapper>
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </ActionsWrapper>
              ) : (
                <></>
              )}
              {onAddSectionAbove && !isChatPageChatWidget(page, s) ? (
                <Tooltip
                  title={
                    <FormattedMessage id="customer_portal.builder.add_section" />
                  }
                >
                  <AddButton
                    data-cy={`customer_portal_builder.add_section_above.${s?.sectionName}`}
                    className="rl-cp-add-section-above"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddSectionAbove?.(s?.sectionReferenceId);
                    }}
                  >
                    <Add20 />
                  </AddButton>
                </Tooltip>
              ) : (
                <></>
              )}
              <MaskSection
                data-section-page-type={page?.pageType}
                id={s?.sectionReferenceId}
                isBuildMode={isBuildMode}
                onClick={() => {
                  // Hack way to prevent clicking on chat section
                  const block = s?.blocks?.[0];
                  const widget = block?.widget;
                  const widgetType = widget?.widgetType;

                  const pageType = page?.pageType;

                  if (
                    pageType === CustomerPortalPageType.Chat &&
                    widgetType === CustomerPortalWidgetType.CHATS
                  ) {
                    return;
                  }
                  onSectionClick?.(s?.sectionReferenceId);
                }}
                className={
                  s?.sectionReferenceId === selectedSection
                    ? 'rl-cp-selected-section'
                    : ''
                }
              >
                <CpSectionRenderer
                  projectId={projectId}
                  section={s}
                  key={s?.sectionReferenceId}
                  page={page}
                  nextSection={sections?.[index + 1]}
                  dontShowDivider={index === sections?.length - 1}
                />
              </MaskSection>
              {onAddSectionBelow && !isChatPageChatWidget(page, s) ? (
                <Tooltip
                  title={
                    <FormattedMessage id="customer_portal.builder.add_section" />
                  }
                >
                  <AddButtonBelow
                    data-cy={`customer_portal_builder.add_section_below.${s?.sectionName}`}
                    className="rl-cp-add-section-below"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddSectionBelow?.(s?.sectionReferenceId);
                    }}
                  >
                    <Add20 />
                  </AddButtonBelow>
                </Tooltip>
              ) : (
                <></>
              )}
            </Wrapper>
          ))
        : emptyPlaceholder ?? <></>}
    </CpPageWrapper>
  );
};
